'use client';

import { PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { AnimatePresence, m } from 'motion/react';
import { useId, useState } from 'react';

import { Container, SectionHeading } from './primitives';

export function FAQ({ heading, items }: { heading: string; items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  const base = useId();

  return (
    <section id="faq" aria-labelledby="faq-heading" className="scroll-mt-24">
      <Container className="grid grid-cols-1 gap-10 pb-20 sm:pb-28 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-4">
          <SectionHeading id="faq-heading" label="Questions" title={heading} />
        </div>
        <div className="rounded-[1.75rem] bg-white px-6 sm:px-8 lg:col-span-8 lg:px-10">
          {items.map((item, i) => {
            const isOpen = open === i;
            const panelId = `${base}-panel-${i}`;
            const buttonId = `${base}-button-${i}`;
            return (
              <div key={item.q} className="rule-ink border-b last:border-b-0">
                <h3>
                  <button
                    id={buttonId}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="flex w-full cursor-pointer items-start justify-between gap-6 py-6 text-left"
                  >
                    <span className="font-display text-pretty text-[1.2rem] leading-[1.25] font-bold tracking-[-0.015em] text-ink-900 sm:text-[1.35rem]">
                      {item.q}
                    </span>
                    <span
                      aria-hidden="true"
                      className={clsx(
                        'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors duration-200',
                        isOpen ? 'bg-beacon-500 text-white' : 'bg-frost-100 text-ink-900',
                      )}
                    >
                      <PlusIcon
                        className={clsx('h-4 w-4 transition-transform duration-300 ease-out', isOpen && 'rotate-45')}
                      />
                    </span>
                  </button>
                </h3>
                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <m.div
                      key="panel"
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-[38rem] text-pretty pb-7 text-[1.05rem] leading-relaxed text-muted">
                        {item.a}
                      </p>
                    </m.div>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
